import useUiTranslation from "../utils/useUiTranslation";
// src/components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useDarkMode from "../hooks/useDarkMode";
import {
  Menu,
  X,
  Sun,
  Moon,
  Contrast,
  ChevronDown,
  Search,
  XCircle,
  Blocks,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import NotificationBell from "./ui/NotificationBell";
import { useAuth } from "../hooks/useAuth";
import { useAccessControl } from "../hooks/useAccessControl";
import { useTheme } from "../theme/ThemeProvider";
import { featureRegistry } from "../config/featureRegistry";
import { hasPermission } from "../utils/permissions";

const Navbar = () => {
  const uiText = useUiTranslation();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isLoading } = useAuth();
  const { hasAccess } = useAccessControl();
  const [darkMode, toggleDarkMode] = useDarkMode();
  const { theme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [isTouchInteraction, setIsTouchInteraction] = useState(false);
  const [navSearch, setNavSearch] = useState("");
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const handlePointerDown = (event) => {
      setIsTouchInteraction(event.pointerType === "touch");
    };

    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  const handleGroupMouseEnter = (groupId) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setOpenGroup(groupId);
  };

  const handleGroupMouseLeave = (groupId) => {
    if (isTouchInteraction) {
      return;
    }

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setOpenGroup((current) => (current === groupId ? null : current));
      hoverTimeoutRef.current = null;
    }, 150);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenGroup(null);
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleLogout = () => {
    logout();
  };

  const getInitials = (name) =>
    name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightMatch = (label) => {
    const trimmedQuery = navSearch.trim();

    if (!trimmedQuery) {
      return label;
    }

    const queryRegex = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "ig");
    return label.split(queryRegex).map((part, index) =>
      part.toLowerCase() === trimmedQuery.toLowerCase() ? (
        <mark
          key={`${part}-${index}`}
          className="rounded bg-yellow-200 px-1 py-0.5 text-gray-900"
        >
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      ),
    );
  };

  const renderNavButton = (
    label,
    path,
    color = "text-black",
    extraClasses = "",
  ) => {
    const isActive =
      location.pathname === path ||
      (path !== "/" && location.pathname.startsWith(`${path}/`));

    const baseClasses =
      "flex items-center gap-2 font-semibold text-sm md:text-base px-3 py-2 rounded-md transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900";

    const activeClasses =
      "bg-white text-blue-600 shadow-sm dark:bg-gray-700 dark:text-blue-300";
    const inactiveClasses = `${color} hover:bg-gray-200 dark:hover:bg-gray-700`;

    return (
      <button
        type="button"
        onClick={() => {
          navigate(path);
          setOpenGroup(null);
          setIsOpen(false);
        }}
        className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses} ${extraClasses}`}
        aria-current={isActive ? "page" : undefined}
      >
        {label}
      </button>
    );
  };

  const NavItems = ({ variant = "desktop" }) => {
  const uiText = useUiTranslation();
    if (isLoading || !user) return null;

    const normalizedRole = user.role?.toLowerCase?.() ?? "";
    const currentUser = user;
    const canViewAllMaintenanceRequests = hasPermission(
      currentUser,
      "maintenance-requests.view-all",
    );

    const canViewAllRequests = hasAccess(currentUser, "feature.allRequests", [
      "requests.view-all",
    ]);
    const canImportHistorical = hasAccess(
      currentUser,
      "feature.historicalRequests",
      ["requests.manage"],
    );
    const canManageProcurement = hasAccess(
      currentUser,
      "feature.procurementPlanning",
      ["planning.view", "planning.manage"],
    );
    const canHandleProcurementQueues = hasAccess(
      currentUser,
      "feature.procurementQueues",
      ["procurement.update-status"],
    );
    const canViewDepartmentRequestedItems = [
      "admin",
      "scm",
      "procurementsupervisor",
    ].includes(normalizedRole);
    const canAccessProcureToPayReceipts = hasAccess(
      currentUser,
      "feature.procureToPayReceipts",
      ["procure-to-pay.receipts.manage"],
    );
    const canAccessProcureToPayInvoices = hasAccess(
      currentUser,
      "feature.procureToPayInvoices",
      ["procure-to-pay.invoices.manage"],
    );
    const canAccessProcureToPayPurchaseOrders = hasAccess(
      currentUser,
      "feature.procurementPlans",
      ["procure-to-pay.purchase-orders.manage"],
    );
    const canAccessProcureToPayMatching = hasAccess(
      currentUser,
      "feature.procureToPayInvoices",
      ["procure-to-pay.match.manage"],
    );
    const canAccessProcureToPayAP = hasAccess(
      currentUser,
      "feature.procureToPayInvoices",
      ["finance.verify"],
    );
    const canAccessProcureToPayPayments = hasAccess(
      currentUser,
      "feature.procureToPayInvoices",
      ["finance.payment.manage"],
    );
    const canAccessProcureToPayLifecycle = hasAccess(
      currentUser,
      "feature.procureToPayInvoices",
      ["procure-to-pay.lifecycle.view"],
    );
    const canAccessCustody = hasAccess(currentUser, "feature.custody", [
      "warehouse.manage-supply",
    ]);
    const canAccessFixedAssets = hasPermission(
      currentUser,
      "fixed-assets.view",
    );
    const canManageTechnicalInspections = hasAccess(
      currentUser,
      "feature.technicalInspections",
      ["warehouse.manage-supply"],
    );
    const canManageWarehouseInventory = hasAccess(
      currentUser,
      "feature.warehouseInventory",
      ["warehouse.manage-supply", "warehouse.view-supply"],
      true,
    );
    const canManageWarehouseTemplates = hasAccess(
      currentUser,
      "feature.warehouseTemplates",
      ["warehouse.manage-supply"],
    );
    const canViewWarehouseRequests = hasAccess(
      currentUser,
      "feature.warehouseRequests",
      ["warehouse.view-supply", "warehouse.manage-supply"],
    );
    const canViewItemMaster = hasAccess(currentUser, "feature.itemMaster", []);
    const canViewSpareParts = hasPermission(currentUser, "spare-parts.view");
    const canMapStockItems = hasAccess(currentUser, null, [
      "item-master.stock-map",
    ]);
    const canViewRecalls = hasAccess(currentUser, "feature.itemRecalls", [
      "recalls.view",
      "recalls.manage",
    ]);
    const canUseAdminTools = hasAccess(currentUser, "feature.adminTools", [
      "approvals.reassign",
    ]);
    const canAccessManagement = hasAccess(currentUser, "feature.management", [
      "users.manage",
      "departments.manage",
      "permissions.manage",
      "projects.manage",
    ]);
    const canUseAi = hasPermission(currentUser, "ai-intelligence.use");
    const canViewDashboard = hasAccess(currentUser, "feature.dashboard", [
      "dashboard.view",
    ]);
    const canRankDepartmentPriorities = hasPermission(
      currentUser,
      "procurement-priority.rank-department",
    );
    const canManageProcurementPriorities = hasPermission(
      currentUser,
      "procurement-priority.manage",
    );
    const canAccessBudgetControl = hasPermission(currentUser,"procure-to-pay.lifecycle.view");
    const canViewAnalytics = hasAccess(currentUser, "feature.analytics", [
      "dashboard.view",
    ]);
    const canViewDispensing = hasAccess(currentUser, "feature.dispensing", [
      "dashboard.view",
      "warehouse.manage-supply",
    ]);
    const canRunPlanning =
      ["planning.view", "planning.manage", "replenishment.manage"].some(p => hasPermission(currentUser,p)) &&
      hasAccess(currentUser, "feature.demandPlanning", ["planning.view", "planning.manage", "replenishment.manage"]);
    const canManageContracts = hasAccess(currentUser, "feature.contracts", [
      "contracts.manage",
    ]);
    const canViewSuppliers = hasAccess(currentUser, "feature.suppliers", [
      "contracts.manage",
    ]);
    const canManageRisks = hasAccess(currentUser, "feature.riskManagement", [
      "risks.view",
      "risks.manage",
    ]);
    const canAccessRfxPortal = hasAccess(currentUser, "feature.rfxPortal", [
      "rfx.manage",
    ]);
    const canAccessSupplierPortalReadiness = hasAccess(
      currentUser,
      "feature.suppliers",
      ["contracts.manage"],
    );
    const canManageEvaluations = hasAccess(
      currentUser,
      "feature.supplierEvaluations",
      ["evaluations.manage"],
    );
    const canAccessAudit = hasAccess(currentUser, "feature.auditRequests", [
      "requests.view-all",
    ]);
    const canReviewStockItems = hasAccess(
      currentUser,
      "feature.stockItemApprovals",
      ["stock-requests.review", "stock-items.manage"],
    );
    const canViewIncompleteQueues =
      hasAccess(currentUser, "feature.incompleteRequests", [
        "requests.view-incomplete",
      ]) || ["cmo", "coo"].includes(normalizedRole);

    const incompletePath =
      normalizedRole === "cmo"
        ? "/incomplete/medical"
        : normalizedRole === "coo"
          ? "/incomplete/operational"
          : "/incomplete";

    const createItem = (condition, label, path, color) =>
      condition ? { label, path, color } : null;

    const resolveFeatureNavItem = (featureKey, conditionOverride = null) => {
      const feature = featureRegistry[featureKey];
      if (!feature?.nav) return null;

      const condition =
        conditionOverride ??
        hasAccess(
          currentUser,
          feature.resourceKey,
          feature.requiredPermissions ?? [],
          feature.requireAllPermissions,
        );

      if (!condition) return null;

      return {
        label: feature.nav.labelKey
          ? t(feature.nav.labelKey)
          : feature.nav.label,
        path: feature.path,
        color: feature.nav.color ?? "text-black",
      };
    };

    const navGroups = [
      {
        id: "requests",
        label: t("navbar.groups.requests"),
        items: [
          resolveFeatureNavItem("openRequests", true),
          resolveFeatureNavItem("approvals", true),
          resolveFeatureNavItem("approvalHistory", true),
          createItem(
            normalizedRole === "technician" || canViewAllMaintenanceRequests,
            canViewAllMaintenanceRequests
              ? t("navbar.engineerMaintenanceStatus")
              : t("navbar.myMaintenance"),
            "/my-maintenance-requests",
            "text-orange-600",
          ),
          createItem(
            true,
            t("navbar.itPrintServiceRequests"),
            "/print-service-requests",
            "text-cyan-700",
          ),
          createItem(
            true,
            t("navbar.closedRequests"),
            "/closed-requests",
            "text-gray-600",
          ),
          createItem(
            hasAccess(currentUser, "feature.auditRequests", [
              "requests.view-audit",
            ]),
            t("navbar.myAuditRegistry"),
            "/audit-registry",
            "text-blue-600",
          ),
          createItem(true,t("navbar.surgicalConsignments",{defaultValue:"Surgical consignment requests"}),"/surgical-consignments","text-indigo-700"),
          resolveFeatureNavItem("allRequests", canViewAllRequests),
          resolveFeatureNavItem("historicalRequests", canImportHistorical),
          resolveFeatureNavItem("procurementPlans", canManageProcurement),
          createItem(
            hasPermission(currentUser,"evaluations.manage"),
            t("navbar.procurementEvaluations"),
            "/procurement-evaluations",
            "text-fuchsia-700",
          ),
          createItem(
            canViewDepartmentRequestedItems,
            t("navbar.departmentRequestedItems"),
            "/department-requested-items",
            "text-indigo-700",
          ),
          resolveFeatureNavItem("assignedRequests", canHandleProcurementQueues),
          resolveFeatureNavItem(
            "completedAssigned",
            canHandleProcurementQueues,
          ),
        ].filter(Boolean),
      },
      {
        id: "procureToPay",
        label: t("navbar.groups.procureToPay"),
        items: [
          createItem(
            canAccessProcureToPayLifecycle,
            t("navbar.procureToPayDashboard"),
            "/procure-to-pay",
            "text-violet-800",
          ),
          createItem(
            canAccessProcureToPayLifecycle,
            t("navbar.procureToPayLifecycle"),
            "/procure-to-pay/lifecycle",
            "text-violet-700",
          ),
          createItem(
            canAccessProcureToPayPurchaseOrders,
            t("navbar.procureToPayPurchaseOrders"),
            "/procure-to-pay/purchase-orders",
            "text-slate-700",
          ),
          createItem(
            canAccessProcureToPayReceipts,
            t("navbar.procureToPayReceipts"),
            "/procure-to-pay/receipts",
            "text-blue-700",
          ),
          createItem(
            canAccessProcureToPayInvoices,
            t("navbar.procureToPayInvoices"),
            "/procure-to-pay/invoices",
            "text-indigo-700",
          ),
          createItem(
            canAccessProcureToPayMatching,
            t("navbar.procureToPayMatching"),
            "/procure-to-pay/matching",
            "text-amber-700",
          ),
          createItem(
            canAccessProcureToPayAP,
            t("navbar.procureToPayAccountsPayable"),
            "/procure-to-pay/accounts-payable",
            "text-cyan-700",
          ),
          createItem(
            canAccessProcureToPayPayments,
            t("navbar.procureToPayPayments"),
            "/procure-to-pay/payments",
            "text-emerald-700",
          ),
          createItem(
            canAccessProcureToPayLifecycle,
            t("navbar.procureToPayDocumentFlow"),
            "/procure-to-pay/document-flow",
            "text-purple-700",
          ),
        ].filter(Boolean),
      },
      {
        id: "fixedAssets",
        label: uiText("FIXED ASSETS"),
        items: [
          createItem(
            canAccessFixedAssets,
            uiText("Dashboard"),
            "/fixed-assets/dashboard",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Asset Register"),
            "/fixed-assets/register",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Register Asset"),
            "/fixed-assets/new",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Movements"),
            "/fixed-assets/movements",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Physical Locations"),
            "/fixed-assets/locations",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Physical Inventory"),
            "/fixed-assets/inventory",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Reconciliation"),
            "/fixed-assets/reconciliation",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("RFID Events"),
            "/fixed-assets/events",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("RFID Readers"),
            "/fixed-assets/readers",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("RFID Portals"),
            "/fixed-assets/portals",
            "text-cyan-700",
          ),
          createItem(
            canAccessFixedAssets,
            uiText("Exceptions"),
            "/fixed-assets/exceptions",
            "text-rose-700",
          ),
        ].filter(Boolean),
      },
      {
        id: "operations",
        label: t("navbar.groups.operations"),
        items: [
          createItem(
            canAccessCustody,
            t("navbar.custodyIssue"),
            "/custody/issue",
            "text-indigo-600",
          ),
          createItem(
            canAccessCustody,
            t("navbar.custodyIssued"),
            "/custody/issued",
            "text-indigo-500",
          ),
          createItem(
            canAccessCustody,
            t("navbar.custodyApprovals"),
            "/custody/approvals",
            "text-indigo-400",
          ),
          createItem(
            canManageTechnicalInspections,
            t("navbar.technicalReviewsInspections", { defaultValue: "Technical Reviews & Inspections" }),
            "/technical-inspections",
            "text-emerald-600",
          ),
          createItem(
            canManageTechnicalInspections &&
              ["scm", "admin"].includes(normalizedRole),
            t("navbar.scmInspectionsReview"),
            "/technical-inspections/review",
            "text-emerald-700",
          ),
          createItem(
            canManageWarehouseInventory,
            t("navbar.warehouseInventory"),
            "/warehouse-inventory",
            "text-blue-700",
          ),
          createItem(
            canViewWarehouseRequests,
            t("navbar.warehouseSupplyRequests"),
            "/warehouse-supply-requests",
            "text-blue-700",
          ),
          createItem(
            canManageWarehouseTemplates,
            t("navbar.warehouseSupplyTemplates"),
            "/warehouse-supply-templates",
            "text-teal-700",
          ),
          createItem(
            canViewItemMaster,
            t("navbar.itemMaster"),
            "/item-master",
            "text-slate-700",
          ),
          createItem(
            canViewSpareParts,
            t("navbar.spareParts"),
            "/spare-parts",
            "text-indigo-700",
          ),
          createItem(
            canMapStockItems,
            t("navbar.stockItemMappings"),
            "/item-master/stock-mappings",
            "text-slate-600",
          ),
          createItem(
            canReviewStockItems,
            t("navbar.stockItemApprovals"),
            "/stock-item-approvals",
            "text-blue-700",
          ),
          createItem(
            canViewRecalls,
            t("navbar.itemRecalls"),
            "/item-recalls",
            "text-amber-600",
          ),
          createItem(
            canAccessAudit,
            t("navbar.auditRequests"),
            "/audit-requests",
            "text-blue-600",
          ),
        ].filter(Boolean),
      },
      {
        id: "insights",
        label: t("navbar.groups.insights"),
        items: [
          createItem(
            canUseAi,
            <span className="inline-flex items-center gap-2">
              <Sparkles size={16} aria-hidden="true" />{uiText("AI Assistant")}</span>,
            "/ai-assistant",
            "text-blue-700",
          ),
          createItem(
            canViewDashboard,
            t("navbar.dashboard"),
            "/dashboard",
            "text-cyan-600",
          ),
          createItem(
            true,
            uiText("Institutional Priorities"),
            "/procurement-priorities",
            "text-blue-700",
          ),
          createItem(
            canRankDepartmentPriorities,
            uiText("Department Priorities"),
            "/procurement-priorities/department",
            "text-blue-700",
          ),
          createItem(
            canManageProcurementPriorities,
            uiText("Priority Management"),
            "/procurement-priorities/manage",
            "text-blue-800",
          ),
          resolveFeatureNavItem("supplyChainPerformance"),
          createItem(
            canAccessBudgetControl,
            t("navbar.budgetControl"),
            "/budget-control",
            "text-emerald-800",
          ),
          createItem(
            canViewAnalytics,
            t("navbar.lifecycleAnalytics"),
            "/analytics",
            "text-pink-600",
          ),
          createItem(
            canViewAnalytics,
            t("navbar.workloadAnalysis"),
            "/workload",
            "text-orange-600",
          ),
          createItem(
            canViewDispensing,
            t("navbar.monthlyDispensing"),
            "/dispensing",
            "text-purple-600",
          ),
          createItem(
            canRunPlanning,
            t("navbar.demandPlanning"),
            "/planning",
            "text-indigo-600",
          ),
          createItem(
            canUseAdminTools,
            t("navbar.adminTools"),
            "/admin-tools",
            "text-yellow-600",
          ),
          createItem(
            canUseAdminTools,
            uiText("Organization Hierarchy"),
            "/admin/organization",
            "text-emerald-600",
          ),
          resolveFeatureNavItem("approvalPolicies"),
          resolveFeatureNavItem("approvalDelegations"),
          createItem(
            canAccessManagement,
            t("navbar.management"),
            "/management",
            "text-purple-600",
          ),
        ].filter(Boolean),
      },
      {
        id: "governance",
        label: t("navbar.groups.governance"),
        items: [
          createItem(
            canViewSuppliers,
            t("navbar.suppliers"),
            "/suppliers",
            "text-emerald-700",
          ),
          createItem(
            canManageRisks,
            t("navbar.riskManagement"),
            "/risk-management",
            "text-rose-700",
          ),
          createItem(
            canAccessRfxPortal,
            t("navbar.rfxPortal"),
            "/rfx-portal",
            "text-emerald-700",
          ),
          createItem(
            canAccessSupplierPortalReadiness,
            t("navbar.supplierPortalReadiness"),
            "/supplier-portal-readiness",
            "text-emerald-700",
          ),
          createItem(
            canViewSuppliers,
            t("navbar.supplierSrm"),
            "/supplier-srm",
            "text-emerald-700",
          ),
          createItem(
            canManageEvaluations,
            t("navbar.supplierEvaluations"),
            "/supplier-evaluations",
            "text-emerald-700",
          ),
          createItem(
            canManageEvaluations,
            t("navbar.supplierDashboard"),
            "/supplier-dashboard",
            "text-emerald-700",
          ),
          createItem(
            canViewSuppliers,
            t("navbar.supplierPrequalification"),
            "/supplier-prequalification",
            "text-emerald-700",
          ),
          createItem(
            canViewIncompleteQueues,
            t("navbar.viewIncomplete"),
            incompletePath,
            "text-orange-600",
          ),
          createItem(
            canManageContracts,
            t("navbar.contracts"),
            "/contracts",
            "text-emerald-600",
          ),
          createItem(
            canManageContracts,
            t("navbar.contractTemplates"),
            "/contract-templates",
            "text-emerald-600",
          ),
          createItem(
            canManageContracts,
            t("navbar.contractClauses"),
            "/contract-clauses",
            "text-emerald-600",
          ),
          createItem(
            canManageContracts,
            t("navbar.contractApprovals"),
            "/contracts/approvals",
            "text-emerald-600",
          ),
          createItem(
            normalizedRole === "scm",
            t("navbar.scmApprovalStages"),
            "/contracts/approval-stage-monitor",
            "text-indigo-700",
          ),
          createItem(
            normalizedRole === "ceo",
            t("navbar.registerUser"),
            "/register",
            "text-blue-600",
          ),
        ].filter(Boolean),
      },
      {
        id: "account",
        label: t("navbar.groups.account"),
        items: [
          createItem(true, t("navbar.myTasks"), "/tasks", "text-blue-600"),
          createItem(
            true,
            t("navbar.deviceDemos", { defaultValue: "Device demo workspace" }),
            "/device-demos",
            "text-indigo-700",
          ),
          createItem(
            true,
            t("navbar.myTechnicalReviews", { defaultValue: "My technical reviews" }),
            "/technical-inspections",
            "text-indigo-700",
          ),
          createItem(
            true,
            t("navbar.myEvaluations"),
            "/my-evaluations",
            "text-blue-600",
          ),
          createItem(
            true,
            t("navbar.changePassword"),
            "/change-password",
            "text-blue-600",
          ),
        ],
      },
    ].filter((group) => group.items.length > 0);

    const normalizedQuery = navSearch.trim().toLowerCase();

    const filteredGroups = navGroups
      .map((group) => ({
        ...group,
        items: normalizedQuery
          ? group.items.filter((item) =>
              item.label.toLowerCase().includes(normalizedQuery),
            )
          : group.items,
      }))
      .filter((group) => group.items.length > 0);

    const renderProfileCard = () => (
      <div
        className="mt-2 flex w-fit items-center gap-2 rounded bg-white/80 px-3 py-1 text-gray-700 shadow dark:bg-gray-700 dark:text-gray-100 lg:mt-0"
        role="contentinfo"
      >
        <div
          className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-sm font-bold text-white"
          title={user.name}
        >
          {getInitials(user.name)}
        </div>
        <div className="leading-tight">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {user.name}
          </div>
          <div className="text-xs italic text-gray-500 dark:text-gray-400">
            ({user.role})
          </div>
        </div>
      </div>
    );

    const renderLogoutButton = () => (
      <button
        onClick={handleLogout}
        className="text-red-600 transition-colors hover:bg-gray-200 dark:text-red-400 dark:hover:bg-gray-700 rounded px-3 py-2 font-semibold text-center"
      >
        {t("navbar.logout")}
      </button>
    );

    if (variant === "desktop") {
      return (
        <>
          {filteredGroups.map((group) => (
            <div
              key={group.id}
              className="relative"
              onMouseEnter={() => handleGroupMouseEnter(group.id)}
              onMouseLeave={() => handleGroupMouseLeave(group.id)}
            >
              <button
                type="button"
                onClick={() =>
                  setOpenGroup((current) =>
                    current === group.id ? null : group.id,
                  )
                }
                onMouseEnter={() => handleGroupMouseEnter(group.id)}
                onFocus={() => handleGroupMouseEnter(group.id)}
                className={`flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-semibold shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${group.items.some((item) => location.pathname === item.path || (item.path !== "/" && location.pathname.startsWith(`${item.path}/`))) ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200" : "border-transparent bg-white/70 text-gray-700 hover:border-gray-200 hover:bg-white dark:bg-gray-800/70 dark:text-gray-100 dark:hover:bg-gray-800"}`}
                aria-haspopup="true"
                aria-expanded={openGroup === group.id}
              >
                {group.label}
                <ChevronDown
                  size={16}
                  className={`transition-transform ${openGroup === group.id ? "rotate-180" : "rotate-0"}`}
                  aria-hidden="true"
                />
              </button>
              <div
                role="menu"
                className={`absolute left-0 top-full z-20 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-xl border border-gray-200 bg-white/95 p-2 shadow-xl backdrop-blur transition-all dark:border-gray-700 dark:bg-gray-900/95 ${
                  openGroup === group.id
                    ? "visible translate-y-0 opacity-100"
                    : "invisible -translate-y-1 opacity-0"
                }`}
                onMouseEnter={() => handleGroupMouseEnter(group.id)}
                onMouseLeave={() => handleGroupMouseLeave(group.id)}
              >
                <div className="flex flex-col gap-1">
                  {group.items.map((item) => (
                    <div key={item.path}>
                      {renderNavButton(
                        highlightMatch(item.label),
                        item.path,
                        item.color,
                        "w-full text-left",
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {renderProfileCard()}
          {renderLogoutButton()}
          {!filteredGroups.length && navSearch && (
            <p className="rounded-md bg-white/80 px-3 py-2 text-sm text-gray-600 shadow-sm dark:bg-gray-800/80 dark:text-gray-300">
              {t("navbar.noSearchResults")}
            </p>
          )}
        </>
      );
    }

    return (
      <>
        {filteredGroups.map((group) => (
          <div
            key={group.id}
            className="flex flex-col gap-2 rounded-md border border-gray-200/70 bg-white/80 p-3 dark:border-gray-700/70 dark:bg-gray-800/80"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {group.label}
            </span>
            <div className="flex flex-col gap-1">
              {group.items.map((item) => (
                <div key={item.path}>
                  {renderNavButton(
                    highlightMatch(item.label),
                    item.path,
                    item.color,
                    "w-full text-left",
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {renderProfileCard()}
        {renderLogoutButton()}
        {!filteredGroups.length && navSearch && (
          <p className="rounded-md border border-dashed border-gray-300 bg-white/80 px-3 py-2 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300">
            {t("navbar.noSearchResults")}
          </p>
        )}
      </>
    );
  };

  return (
    <nav
      className="sticky top-0 z-50 border-b border-gray-200/80 bg-white/90 shadow-sm backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/90 dark:text-gray-100"
      role="navigation"
      aria-label={t("navbar.mainNavigation")}
    >
      <div className="flex w-full items-center justify-between px-4 py-3 md:py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 p-2.5 text-white shadow-md transition hover:scale-105 hover:shadow-lg"
            aria-label={t("navbar.home", { defaultValue: "Home" })}
            title={t("navbar.home", { defaultValue: "Home" })}
          >
            <Blocks size={20} aria-hidden="true" />
          </button>
          <h1
            className="cursor-pointer text-lg font-bold tracking-tight text-gray-900 dark:text-white md:text-xl"
            onClick={() => navigate("/")}
          >
            {t("navbar.purchaseTracker")}
          </h1>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 focus-within:border-blue-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 md:flex">
            <Search
              size={16}
              className="text-gray-500 dark:text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={navSearch}
              onChange={(event) => setNavSearch(event.target.value)}
              placeholder={t("navbar.searchPlaceholder")}
              className="w-48 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            {navSearch && (
              <button
                type="button"
                onClick={() => setNavSearch("")}
                className="text-gray-500 transition hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                aria-label={t("navbar.clearSearch")}
              >
                <XCircle size={16} />
              </button>
            )}
          </div>
          <div className="hidden flex-wrap items-center justify-center gap-3 lg:flex">
            <NavItems variant="desktop" />
          </div>
          <NotificationBell />
          <div className="flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 px-2 py-1 backdrop-blur dark:border-gray-700 dark:bg-gray-800/80">
            <select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              className="bg-transparent text-sm text-gray-700 focus:outline-none dark:text-gray-200"
              aria-label={t("navbar.selectLanguage")}
            >
              <option value="en">{t("language.english")}</option>
              <option value="ar">{t("language.arabic")}</option>
            </select>
            <span
              className="h-4 w-px bg-gray-300 dark:bg-gray-600"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={toggleDarkMode}
              className="group inline-flex items-center gap-2 rounded-full px-2 py-1 text-gray-700 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:text-gray-200 dark:hover:bg-blue-950/70 dark:hover:text-blue-200"
              aria-label={
                theme === themes.light
                  ? t("navbar.darkMode")
                  : theme === themes.dark
                    ? t("navbar.highContrastMode")
                    : t("navbar.lightMode")
              }
              title={
                theme === themes.light
                  ? t("navbar.darkMode")
                  : theme === themes.dark
                    ? t("navbar.highContrastMode")
                    : t("navbar.lightMode")
              }
            >
              {theme === themes.highContrast ? (
                <Contrast size={18} />
              ) : darkMode ? (
                <Moon size={18} />
              ) : (
                <Sun size={18} />
              )}
              <span className="hidden text-xs font-semibold xl:inline">
                {theme === themes.light
                  ? uiText("Light")
                  : theme === themes.dark
                    ? uiText("Dark")
                    : uiText("Contrast")}
              </span>
            </button>
          </div>

          <button
            className="inline-flex items-center justify-center rounded-md border border-gray-300 p-2 text-gray-700 transition hover:bg-gray-200 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={t("navbar.toggleMenu")}
            aria-expanded={isOpen}
            type="button"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-3 border-t border-gray-200 bg-white/95 px-4 pb-4 pt-2 shadow-inner dark:border-gray-800 dark:bg-gray-900/95 lg:hidden">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
              {t("navbar.navigation")}
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-xs font-medium text-blue-600 dark:text-blue-300"
            >
              {t("navbar.closeMenu")}
            </button>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white/90 px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 dark:border-gray-700 dark:bg-gray-800/90 dark:focus-within:ring-offset-gray-900">
            <Search
              size={16}
              className="text-gray-500 dark:text-gray-400"
              aria-hidden="true"
            />
            <input
              type="text"
              value={navSearch}
              onChange={(event) => setNavSearch(event.target.value)}
              placeholder={t("navbar.searchPlaceholder")}
              className="flex-1 bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
            />
            {navSearch && (
              <button
                type="button"
                onClick={() => setNavSearch("")}
                className="text-gray-500 transition hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                aria-label={t("navbar.clearSearch")}
              >
                <XCircle size={16} />
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <NavItems variant="mobile" />
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
